import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Activity, ArrowLeft, ChevronDown, ChevronUp, FolderKanban, Plus } from 'lucide-react';
import apiService from '../../services/apiService';
import { getActiveForecastUser } from './forecastUsers';

// Corporate Projects home: the project book leads; starting a new project is a
// secondary, collapsible panel. New projects auto-claim an open project
// dimension + Corporate Projects entity server-side (no manual ID picking).

const STATUS_TONES = { draft: 'amber', in_review: 'gold', approved: 'moss', loaded: 'moss', canceled: 'neutral', archived: 'neutral' };

const MATERIALITY_TIERS = ['', 'low', 'medium', 'high', 'strategic'];
const PRIORITY_LEVELS = ['', 'low', 'normal', 'high', 'urgent'];
const COMMITTEE_STAGES = ['', 'idea', 'intake', 'sponsor_review', 'finance_review', 'capital_committee', 'approved', 'deferred', 'rejected'];
const STATUS_FILTERS = ['draft', 'in_review', 'approved', 'loaded'];

// Vercel platform errors arrive as {error: {code, id, message}} — coerce
// anything non-string before it reaches JSX (React error #31 otherwise).
export const errText = (err) => {
  const data = err?.response?.data;
  const candidate = data?.error ?? data?.message ?? err?.message ?? err;
  if (typeof candidate === 'string') return candidate;
  if (candidate && typeof candidate === 'object') {
    return candidate.message || candidate.code || JSON.stringify(candidate).slice(0, 200);
  }
  return String(candidate);
};

const titleCase = (value) => (value || '').replace(/_/g, ' ');

// Client-side sort so column headers stay responsive even when the API returns
// updated_at order. Materiality/priority use an explicit rank; everything else
// is string/number natural order.
const MATERIALITY_RANK = { low: 1, medium: 2, high: 3, strategic: 4 };
const PRIORITY_RANK = { low: 1, normal: 2, high: 3, urgent: 4 };

const SORTERS = {
  name: (p) => (p.name || '').toLowerCase(),
  sponsor: (p) => (p.sponsor || '').toLowerCase(),
  accountableOwner: (p) => (p.accountableOwner || '').toLowerCase(),
  dueDate: (p) => p.dueDate || '',
  materialityTier: (p) => MATERIALITY_RANK[p.materialityTier] || 0,
  priority: (p) => PRIORITY_RANK[p.priority] || 0,
  capitalCommitteeStage: (p) => (p.capitalCommitteeStage || ''),
  status: (p) => (p.status || ''),
  npv: (p) => (p.totals ? Number(p.totals.npvMM) : -Infinity),
  payback: (p) => (p.totals ? Number(p.totals.paybackMonth) || Infinity : Infinity)
};

const ForecastProjects = () => {
  const navigate = useNavigate();
  const activeUser = getActiveForecastUser();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', sponsor: '', accountableOwner: '',
    dueDate: '', materialityTier: '', priority: '', capitalCommitteeStage: ''
  });
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);   // empty = all
  const [sort, setSort] = useState({ key: 'dueDate', dir: 'asc' });

  useEffect(() => {
    apiService.fetchProjects().then(setData).catch((err) => setError(errText(err)));
  }, []);

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      const result = await apiService.createProject({ ...form, actor: activeUser.name });
      navigate(`/forecasting/projects/${result.id}`);
    } catch (err) {
      setError(errText(err));
      setCreating(false);
    }
  };

  const toggleStatus = (status) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]);
  };

  const setSortKey = (key) => {
    setSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  const projects = useMemo(() => {
    let rows = data?.projects || [];
    if (statusFilter.length) rows = rows.filter((p) => statusFilter.includes(p.status));
    const sorter = SORTERS[sort.key] || SORTERS.name;
    rows = [...rows].sort((a, b) => {
      const av = sorter(a);
      const bv = sorter(b);
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [data, statusFilter, sort]);

  const SortableTh = ({ label, sortKey, mono }) => (
    <th
      className={mono ? 'forecast-mono' : undefined}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => setSortKey(sortKey)}
      aria-sort={sort.key === sortKey ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}{sort.key === sortKey ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    <div className="forecast-module forecast-home">
      <section className="forecast-command">
        <div>
          <div className="forecast-kicker">
            <Activity size={14} />
            Forecast Console / Corporate Projects
            <Link to="/forecasting" className="forecast-kicker-back">
              <ArrowLeft size={12} /> Cycle home
            </Link>
          </div>
          <h1>Corporate Projects</h1>
          <p>
            Upload the enterprise project team's business case, let the AI build the first-pass
            pro forma — marginal revenue only, capex bucketed and capitalized — then review,
            edit, and load under a dedicated project dimension and entity.
          </p>
        </div>
      </section>

      {/* Project book leads the page. */}
      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">PRJ_LIST ● Project book</span>
          <h2>
            <FolderKanban size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            {projects.length} corporate project(s)
          </h2>
        </div>

        <div className="forecast-submit-chips" style={{ marginBottom: 10 }} role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map((status) => (
            <button
              key={status}
              type="button"
              className={`forecast-status-chip ${statusFilter.includes(status) ? (STATUS_TONES[status] || 'moss') : 'neutral'}`}
              aria-pressed={statusFilter.includes(status)}
              style={{ cursor: 'pointer', opacity: statusFilter.length && !statusFilter.includes(status) ? 0.55 : 1 }}
              onClick={() => toggleStatus(status)}
            >
              {titleCase(status)}
            </button>
          ))}
          {statusFilter.length > 0 && (
            <button type="button" className="forecast-home-action" onClick={() => setStatusFilter([])}>
              Clear
            </button>
          )}
        </div>

        <div className="forecast-home-table-wrap">
          <table className="forecast-home-table">
            <thead>
              <tr>
                <SortableTh label="Project" sortKey="name" />
                <SortableTh label="Sponsor" sortKey="sponsor" />
                <SortableTh label="Owner" sortKey="accountableOwner" />
                <SortableTh label="Due" sortKey="dueDate" />
                <SortableTh label="Materiality" sortKey="materialityTier" />
                <SortableTh label="Priority" sortKey="priority" />
                <SortableTh label="Committee" sortKey="capitalCommitteeStage" />
                <SortableTh label="Status" sortKey="status" />
                <SortableTh label="NPV" sortKey="npv" mono />
                <SortableTh label="Payback" sortKey="payback" mono />
                <th />
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id}>
                  <td><strong>{project.name}</strong>
                    <span className="forecast-home-market"> {project.description ? `· ${project.description}` : ''}</span>
                  </td>
                  <td>{project.sponsor || '—'}</td>
                  <td>{project.accountableOwner || '—'}</td>
                  <td className="forecast-mono">{project.dueDate || '—'}</td>
                  <td>{project.materialityTier ? titleCase(project.materialityTier) : '—'}</td>
                  <td>{project.priority ? titleCase(project.priority) : '—'}</td>
                  <td>{project.capitalCommitteeStage ? titleCase(project.capitalCommitteeStage) : '—'}</td>
                  <td>
                    <span className={`forecast-status-chip ${STATUS_TONES[project.status] || 'neutral'}`}>
                      {titleCase(project.status)}
                    </span>
                  </td>
                  <td className={`forecast-mono ${project.totals?.npvMM >= 0 ? 'pos' : 'neg'}`}>
                    {project.totals ? `$${Number(project.totals.npvMM).toFixed(1)}MM` : '—'}
                  </td>
                  <td className="forecast-mono">{project.totals?.paybackLabel || '—'}</td>
                  <td>
                    <button type="button" className="forecast-home-action"
                      onClick={() => navigate(`/forecasting/projects/${project.id}`)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="forecast-submit-note">
          Project forecasts load to GL_Fact under the project dimension and a dedicated
          Corporate Projects entity — they never touch market planning entities.
        </p>
      </section>

      {/* Creation moved to a collapsible secondary panel. */}
      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">PRJ_00 ● Start a new project</span>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="forecast-secondary-button compact"
              aria-expanded={createOpen}
              onClick={() => setCreateOpen((open) => !open)}
            >
              {createOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {createOpen ? 'Hide' : 'New project'}
            </button>
            <span style={{ fontSize: '0.9em', fontWeight: 400 }}>
              A project dimension and Corporate Projects entity are assigned automatically.
            </span>
          </h2>
        </div>
        {createOpen && (
          <>
            <div className="project-create-grid">
              <label>
                <span>Project name</span>
                <input
                  type="text" value={form.name} placeholder="e.g. Project Falcon — LOS Replacement"
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>
              <label>
                <span>Sponsor</span>
                <input
                  type="text" value={form.sponsor} placeholder="e.g. Commercial Banking"
                  onChange={(event) => setForm({ ...form, sponsor: event.target.value })}
                />
              </label>
              <label>
                <span>Accountable owner</span>
                <input
                  type="text" value={form.accountableOwner} placeholder="e.g. Dana Whitfield"
                  onChange={(event) => setForm({ ...form, accountableOwner: event.target.value })}
                />
              </label>
              <label>
                <span>Due date</span>
                <input
                  type="date" value={form.dueDate}
                  onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
                />
              </label>
              <label>
                <span>Materiality tier</span>
                <select value={form.materialityTier} onChange={(event) => setForm({ ...form, materialityTier: event.target.value })}>
                  {MATERIALITY_TIERS.map((tier) => <option key={tier} value={tier}>{tier ? titleCase(tier) : '—'}</option>)}
                </select>
              </label>
              <label>
                <span>Priority</span>
                <select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })}>
                  {PRIORITY_LEVELS.map((level) => <option key={level} value={level}>{level ? titleCase(level) : '—'}</option>)}
                </select>
              </label>
              <label>
                <span>Capital committee stage</span>
                <select value={form.capitalCommitteeStage} onChange={(event) => setForm({ ...form, capitalCommitteeStage: event.target.value })}>
                  {COMMITTEE_STAGES.map((stage) => <option key={stage} value={stage}>{stage ? titleCase(stage) : '—'}</option>)}
                </select>
              </label>
              <label className="project-create-desc">
                <span>Description (names the project dimension)</span>
                <input
                  type="text" value={form.description} placeholder="One-line description for the dimension record"
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                />
              </label>
              <button
                type="button" className="forecast-primary-button"
                disabled={creating || !form.name}
                onClick={create}
              >
                <Plus size={15} /> {creating ? 'Creating…' : 'Create project'}
              </button>
            </div>
          </>
        )}
        {error && <p className="forecast-submit-note" style={{ color: 'var(--ace-red, #c4554d)' }}>{error}</p>}
      </section>
    </div>
  );
};

export default ForecastProjects;
